import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Save, RefreshCw } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

/**
 * Edit Model dialog — extracted from SemanticModelsPage.
 * Edits the active model's name, status, and AI persona/context.
 */
export function EditModelDialog({
  open, onOpenChange,
  model, setModel,
  saving,
  smHasUnsavedChanges, setSmHasUnsavedChanges,
  loadModel, activeModelId,
  onSave,
  entities = [],
}) {
  const factEntities = (entities || []).filter(e => e.entity_type === 'fact');
  const freshCfg = model?.data_freshness || {};
  const selFactEntity = factEntities.find(e => e.entity_id === freshCfg.entity_id);
  const dateAttrs = ((selFactEntity?.attributes) || []).filter(a => {
    const n = (a.name || '').toUpperCase();
    const t = (a.data_type || '').toLowerCase();
    return n.includes('DATE') || t.includes('date') || t.includes('time');
  });
  const fieldOptions = dateAttrs.length ? dateAttrs : ((selFactEntity?.attributes) || []);
  const [flushing, setFlushing] = React.useState(false);
  const mid = model?.model_id || activeModelId;
  const flushCache = async () => {
    if (!mid) return;
    setFlushing(true);
    try {
      const r = await api.flushExploreCache(mid);
      toast.success(`Cleared ${r.deleted || 0} cached answer${(r.deleted || 0) === 1 ? '' : 's'}. AI Explore will recompute from live data (incl. the "Data through" date) on the next question.`);
    } catch (e) {
      toast.error(e?.message || 'Could not clear the cache');
    } finally {
      setFlushing(false);
    }
  };
  const setFreshness = (patch) => {
    setSmHasUnsavedChanges(true);
    setModel(m => {
      const cur = m.data_freshness || {};
      const next = { ...cur, ...patch };
      if (!next.entity_id || !next.field) return { ...m, data_freshness: next.entity_id ? next : null };
      return { ...m, data_freshness: next };
    });
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v && smHasUnsavedChanges) {
          loadModel(activeModelId);
          setSmHasUnsavedChanges(false);
        }
      }}
    >
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Edit Model</DialogTitle></DialogHeader>
        {model && (
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs text-muted-foreground">Name</Label>
              <Input
                value={model.name}
                onChange={e => { setSmHasUnsavedChanges(true); setModel(m => ({ ...m, name: e.target.value })); }}
                className="rounded-lg bg-muted/50 border-border/50 mt-1"
                data-testid="edit-model-name"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Description</Label>
              <p className="text-[10px] text-muted-foreground/80 mt-0.5 mb-1">Shown to business users in the model list and AI Explore picker</p>
              <textarea
                value={model.description || ''}
                onChange={e => { setSmHasUnsavedChanges(true); setModel(m => ({ ...m, description: e.target.value })); }}
                placeholder="e.g. Policy, premium and loss data for the P&C book — certified for underwriting and claims reporting."
                className="w-full rounded-lg bg-muted/50 border border-border/50 p-2 text-sm min-h-[64px] resize-none outline-none focus:ring-1 focus:ring-primary/50"
                data-testid="edit-model-description"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Status</Label>
              <Select value={model.status} onValueChange={v => { setSmHasUnsavedChanges(true); setModel(m => ({ ...m, status: v })); }}>
                <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Fiscal Year Starts</Label>
              <p className="text-[10px] text-muted-foreground/80 mt-0.5 mb-1">Used for YTD/QTD to-date totals and the year/quarter date-range presets</p>
              <Select
                value={String(model.fiscal_year_start_month || 1)}
                onValueChange={v => { setSmHasUnsavedChanges(true); setModel(m => ({ ...m, fiscal_year_start_month: parseInt(v, 10) })); }}
              >
                <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-1" data-testid="edit-model-fiscal-start"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((mo, i) => (
                    <SelectItem key={mo} value={String(i + 1)}>{mo}{i === 0 ? ' (calendar)' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Week Starts</Label>
              <p className="text-[10px] text-muted-foreground/80 mt-0.5 mb-1">Used for WTD (week-to-date) totals</p>
              <Select
                value={String(model.week_start_day ?? 0)}
                onValueChange={v => { setSmHasUnsavedChanges(true); setModel(m => ({ ...m, week_start_day: parseInt(v, 10) })); }}
              >
                <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-1" data-testid="edit-model-week-start"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, i) => (
                    <SelectItem key={day} value={String(i)}>{day}{i === 0 ? ' (ISO default)' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Data Freshness Field</Label>
              <p className="text-[10px] text-muted-foreground/80 mt-0.5 mb-1">Pick a load-date column; AI Explore's "Data through" date becomes the latest value of this field (MAX), refreshed on the first query each day. Overrides any static date.</p>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <Select
                  value={freshCfg.entity_id || '__none__'}
                  onValueChange={v => {
                    if (v === '__none__') { setSmHasUnsavedChanges(true); setModel(m => ({ ...m, data_freshness: null })); }
                    else setFreshness({ entity_id: v, field: undefined });
                  }}
                >
                  <SelectTrigger className="rounded-lg bg-muted/50 border-border/50" data-testid="edit-model-freshness-entity"><SelectValue placeholder="Table" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None (use static date)</SelectItem>
                    {factEntities.map(e => (
                      <SelectItem key={e.entity_id} value={e.entity_id}>{e.name}{e.table_name && e.table_name !== e.name ? ` (${e.table_name})` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={freshCfg.field || ''}
                  disabled={!freshCfg.entity_id}
                  onValueChange={v => setFreshness({ field: v })}
                >
                  <SelectTrigger className="rounded-lg bg-muted/50 border-border/50" data-testid="edit-model-freshness-field"><SelectValue placeholder="Date field" /></SelectTrigger>
                  <SelectContent>
                    {fieldOptions.map(a => (
                      <SelectItem key={a.name} value={a.name}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between gap-2 mt-2 rounded-lg border border-border/40 bg-muted/30 p-2">
                <p className="text-[10px] text-muted-foreground/90 leading-snug">
                  Seeing an old "Data through" date or stale results? Clear this model's cached AI Explore answers so the next question recomputes from live data.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={flushCache}
                  disabled={flushing || !mid}
                  className="shrink-0 rounded-lg text-xs"
                  data-testid="edit-model-flush-cache"
                >
                  <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${flushing ? 'animate-spin' : ''}`} />
                  {flushing ? 'Clearing…' : 'Clear cached answers'}
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">AI Persona / Context</Label>
              <p className="text-[10px] text-muted-foreground/80 mt-0.5 mb-1">Give the Explore AI a role and domain expertise for this model</p>
              <textarea
                value={model.ai_persona || ''}
                onChange={e => { setSmHasUnsavedChanges(true); setModel(m => ({ ...m, ai_persona: e.target.value })); }}
                placeholder="e.g. You are a P&C insurance executive advisor. Focus on loss ratios, combined ratios, and underwriting profitability. A combined ratio below 100% indicates profitable underwriting."
                className="w-full rounded-lg bg-muted/50 border border-border/50 p-2 text-sm min-h-[80px] resize-none outline-none focus:ring-1 focus:ring-primary/50"
                data-testid="edit-model-persona"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">AI Answerability Strictness</Label>
              <p className="text-[10px] text-muted-foreground/80 mt-0.5 mb-1">How aggressively AI Explore declines derived/ratio questions this model can't truly answer (vs. guessing a proxy)</p>
              <Select
                value={model.answerability_strictness || 'balanced'}
                onValueChange={v => { setSmHasUnsavedChanges(true); setModel(m => ({ ...m, answerability_strictness: v })); }}
              >
                <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-1" data-testid="edit-model-answerability"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="strict">Strict — decline anything not a defined measure</SelectItem>
                  <SelectItem value="balanced">Balanced — decline only when it would guess a proxy (recommended)</SelectItem>
                  <SelectItem value="lenient">Lenient — answer unless the concept is entirely missing</SelectItem>
                  <SelectItem value="off">Off — never auto-decline (not recommended)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={onSave} disabled={saving} className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95">
              <Save className="w-4 h-4 mr-2" />Save Changes
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
